import {Flag} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../../i18n/i18n';
import {VEHICLETYPE_PASSENGER_CAR} from '../../../Create/constants/vehiclePassengerCar';
import {IMPORT_VEHICLE_NR_COLUMN_WIDTH} from '../../constants/importVehicleNrColumnWidth';
import {ImportVehicle} from '../../types/ImportVehicle';

interface ImportVehicleSummaryProps extends TestIdProps {
  vehicle: ImportVehicle;
}

export function ImportVehicleSummary(props: ImportVehicleSummaryProps) {
  const catalogue = useCatalogue();
  const {data: translatedInfo} = useVehicleTranslatedInfo(
    props.vehicle.make != null
      ? {
          make: props.vehicle.make,
          modelFamily: props.vehicle.model ?? undefined,
          vehicleType: VEHICLETYPE_PASSENGER_CAR,
        }
      : null
  );
  const makePart = translatedInfo?.make ?? props.vehicle.make ?? '';
  const modelPart = translatedInfo?.model ?? props.vehicle.model ?? '';
  const hasMakeOrModel = makePart.trim() !== '' || modelPart.trim() !== '';
  const displayNameRaw = hasMakeOrModel
    ? `${makePart} ${modelPart}`.trim() +
      (isNotNil(props.vehicle.year) ? ` ${props.vehicle.year}` : '')
    : props.vehicle.name;
  const displayName = displayNameRaw.trim() === '' ? EMPTY_PLACEHOLDER : displayNameRaw;

  const displaySpecs = (() => {
    const v = props.vehicle;
    const parts: string[] = [];
    if (v.power != null) {
      parts.push(`${v.power} kW`);
    }
    if (v.transmission) {
      parts.push(catalogue.getTransmission(v.transmission));
    }
    if (v.drive) {
      parts.push(catalogue.getDrive(v.drive));
    }
    if (v.fuel) {
      parts.push(catalogue.getFuelType(v.fuel));
    }
    if (v.mileage != null) {
      parts.push(`${v.mileage.toLocaleString()} km`);
    }
    return parts.length > 0 ? parts.join(' • ') : v.specs;
  })();

  return (
    <HStack spacing={4} align="center" data-testid={suffixTestId('vehicleSummary', props)}>
      <Box flexShrink={0} width={IMPORT_VEHICLE_NR_COLUMN_WIDTH}>
        <Text size="xSmall" color="secondary" noWrap>
          {props.vehicle.nr}
        </Text>
      </Box>
      <VStack spacing={1}>
        <Text size="base" color="primary">
          {displayName}
        </Text>
        <HStack spacing={2} align="center">
          <Show when={displaySpecs.trim() !== ''}>
            <Text size="xSmall" color="secondary">
              {displaySpecs}
              {props.vehicle.hasVat ? ' • ' : null}
            </Text>
          </Show>
          {props.vehicle.hasVat && (
            <Flag label={i18n.t('general.labels.vat')} size="small" colorScheme="black" />
          )}
        </HStack>
      </VStack>
    </HStack>
  );
}
